import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Save, ArrowLeft, Trash2, Upload, FileText, Clipboard, Package } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import RichEditor from '../components/RichEditor';
import ApiService from '../services/api';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const AdminEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contentType = searchParams.get('type') || 'script';
  const { scripts, articles, faqs, tools, addScript, addArticle, addFaq, addTool, updateScript, updateArticle, updateFaq, updateTool, deleteScript, deleteArticle, deleteFaq, deleteTool } = useData();
  const { user: currentUser, isAdmin } = useAuth();
  
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [uploadingToolFile, setUploadingToolFile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Performance',
    subcategory: '',
    content: '',
    author: 'LYSH DBA Team',
    readTime: '10 min read',
    question: '',
    answer: '',
    difficulty: 'Intermediate',
    version: '1.0',
    platform: 'Windows',
    fileName: '',
    fileUrl: '',
    fileSize: ''
  });

  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing) {
      const item = contentType === 'script' 
        ? scripts.find(s => s.id === id)
        : contentType === 'article' 
        ? articles.find(a => a.id === id)
        : contentType === 'tool'
        ? tools.find(t => t.id === id)
        : faqs.find(f => f.id === id);
        if (item) {
        // Check if user can edit this content
        const ownerId = item.author_id ?? item.authorId;
        if (!isAdmin && String(ownerId) !== String(currentUser.id)) {
          alert('You can only edit your own content');
          navigate('/admin');
          return;
        }
        setFormData({
          title: item.title || '',
          description: item.description || '',
          category: item.category,
          subcategory: item.subcategory,
          content: item.content || '',
          author: item.author || 'LYSH DBA Team',
          readTime: item.readTime || item.read_time || '10 min read',
          question: item.question || '',
          answer: item.answer || '',
          difficulty: item.difficulty || 'Intermediate',
          version: item.version || '1.0',
          platform: item.platform || 'Windows',
          fileName: item.file_name || '',
          fileUrl: item.file_url || '',
          fileSize: item.file_size || ''
        });
      }
    }
  }, [id, scripts, articles, faqs, tools, isEditing, contentType, isAdmin, currentUser.id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        if (contentType === 'script') {
          await updateScript(id, formData);
        } else if (contentType === 'article') {
          await updateArticle(id, formData);
        } else if (contentType === 'tool') {
          await updateTool(id, formData);
        } else {
          await updateFaq(id, formData);
        }
      } else {
        if (contentType === 'script') {
          await addScript(formData, currentUser.id);
        } else if (contentType === 'article') {
          await addArticle(formData, currentUser.id);
        } else if (contentType === 'tool') {
          await addTool(formData, currentUser.id);
        } else {
          await addFaq(formData, currentUser.id);
        }
      }
      
      navigate('/admin');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save. Please try again.');
    }
  };

  const categories = {
    'Performance': ['Comprehensive', 'CPU', 'Memory', 'IO'],
    'Security': ['Permissions', 'Authentication', 'Encryption'],
    'High Availability': ['Always On', 'Backup', 'Replication'],
    'Backup and Recovery': ['Backup Strategies', 'Recovery Procedures', 'Backup Monitoring'],
    'Automation': ['Job Scheduling', 'PowerShell Scripts', 'Maintenance Plans'],
    'Installation': ['Fresh Installation', 'Configuration', 'Validation'],
    'Architecture and Configuration': ['System Design', 'Server Configuration', 'Network Setup'],
    'Missilience': ['Routine Maintenance', 'Index Maintenance', 'Health Monitoring'],
    'Azure SQL': ['Migration', 'Performance', 'Security']
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset subcategory when category changes
      ...(name === 'category' ? { subcategory: '' } : {})
    }));
  };



  const parseMarkdownToBlocks = (markdown) => {
    const lines = markdown.split('\n');
    const blocks = [];
    let currentBlock = null;
    let inCodeBlock = false;
    let codeLanguage = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith('```')) {
        if (inCodeBlock) {
          if (currentBlock) {
            blocks.push(currentBlock);
            currentBlock = null;
          }
          inCodeBlock = false;
        } else {
          if (currentBlock && currentBlock.content) {
            blocks.push(currentBlock);
            currentBlock = null;
          }
          inCodeBlock = true;
          codeLanguage = line.substring(3).trim();
          currentBlock = {
            id: Date.now() + i,
            type: codeLanguage === 'sql' ? 'sql' : 'text',
            content: ''
          };
        }
        continue;
      }

      if (inCodeBlock) {
        currentBlock.content += (currentBlock.content ? '\n' : '') + line;
        continue;
      }

      if (line.startsWith('#')) {
        if (currentBlock && currentBlock.content) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        const level = line.match(/^#+/)[0].length;
        blocks.push({
          id: Date.now() + i,
          type: 'heading',
          content: { content: line.substring(level).trim(), level: `h${Math.min(level, 4)}` }
        });
        continue;
      }

      if (!line.trim()) {
        if (currentBlock && currentBlock.content) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        continue;
      }

      if (!currentBlock) {
        currentBlock = { id: Date.now() + i, type: 'text', content: '' };
      }
      currentBlock.content += (currentBlock.content ? '\n' : '') + line;
    }

    if (currentBlock) blocks.push(currentBlock);
    return blocks.length > 0 ? blocks : [{ id: Date.now(), type: 'text', content: '' }];
  };

  const handleFileUpload = (file, type) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      
      if (type === 'script') {
        setFormData(prev => ({ ...prev, content }));
      } else if (type === 'article' || type === 'tool') {
        const blocks = parseMarkdownToBlocks(content);
        setFormData(prev => ({ ...prev, content: blocks }));
      } else if (type === 'faq') {
        setFormData(prev => ({ ...prev, answer: content }));
      }
    };
    reader.readAsText(file);
  };

  const htmlToBlocks = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const blocks = [];
    let blockId = Date.now();

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          blocks.push({ id: blockId++, type: 'text', content: text });
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const tag = node.tagName.toLowerCase();

      if (['h1', 'h2', 'h3', 'h4'].includes(tag)) {
        blocks.push({
          id: blockId++,
          type: 'heading',
          content: { content: node.textContent.trim(), level: tag }
        });
      } else if (tag === 'pre' || tag === 'code') {
        const codeText = node.textContent.trim();
        if (codeText) {
          blocks.push({ id: blockId++, type: 'sql', content: codeText });
        }
      } else if (tag === 'table') {
        const headers = [];
        const rows = [];
        const headerCells = node.querySelectorAll('thead th, tr:first-child th');
        headerCells.forEach(cell => headers.push(cell.textContent.trim()));
        const dataRows = headers.length > 0 ? node.querySelectorAll('tbody tr, tr:not(:first-child)') : node.querySelectorAll('tr');
        dataRows.forEach(row => {
          const cells = [];
          row.querySelectorAll('td, th').forEach(cell => cells.push(cell.textContent.trim()));
          if (cells.length > 0) rows.push(cells);
        });
        if (headers.length > 0 || rows.length > 0) {
          blocks.push({
            id: blockId++,
            type: 'table',
            content: {
              headers: headers.length > 0 ? headers : (rows[0] || ['Column 1']),
              rows: headers.length > 0 ? rows : rows.slice(1)
            }
          });
        }
      } else if (tag === 'ul' || tag === 'ol') {
        let listText = '';
        const items = node.querySelectorAll(':scope > li');
        items.forEach((li, i) => {
          const prefix = tag === 'ol' ? `${i + 1}. ` : '• ';
          listText += (listText ? '\n' : '') + prefix + li.textContent.trim();
        });
        if (listText) {
          blocks.push({ id: blockId++, type: 'text', content: listText });
        }
      } else if (tag === 'p' || tag === 'div') {
        let text = '';
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            text += child.textContent;
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childTag = child.tagName.toLowerCase();
            const childText = child.textContent;
            if (childTag === 'strong' || childTag === 'b') {
              text += `**${childText}**`;
            } else if (childTag === 'em' || childTag === 'i') {
              text += `*${childText}*`;
            } else if (childTag === 'br') {
              text += '\n';
            } else {
              text += childText;
            }
          }
        });
        text = text.trim();
        if (text) {
          blocks.push({ id: blockId++, type: 'text', content: text });
        }
      } else if (['body', 'section', 'article', 'main', 'span'].includes(tag)) {
        node.childNodes.forEach(child => processNode(child));
      } else {
        const text = node.textContent.trim();
        if (text) {
          blocks.push({ id: blockId++, type: 'text', content: text });
        }
      }
    };

    doc.body.childNodes.forEach(child => processNode(child));
    return blocks.length > 0 ? blocks : [{ id: Date.now(), type: 'text', content: '' }];
  };

  const handleDocUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    if (!['docx', 'pdf'].includes(ext)) {
      alert('Please upload a .docx (Word) or .pdf file.');
      return;
    }

    setUploadingDoc(true);

    try {
      if (ext === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const blocks = htmlToBlocks(result.value);
        setFormData(prev => ({ ...prev, content: blocks }));
        if (result.messages.length > 0) {
          console.warn('Word conversion warnings:', result.messages);
        }
      } else if (ext === 'pdf') {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += (fullText ? '\n\n' : '') + pageText;
        }

        // Parse extracted text into blocks (split by double newlines)
        const paragraphs = fullText.split(/\n{2,}/);
        const blocks = paragraphs
          .map((text, i) => text.trim())
          .filter(text => text)
          .map((text, i) => ({
            id: Date.now() + i,
            type: 'text',
            content: text
          }));

        setFormData(prev => ({
          ...prev,
          content: blocks.length > 0 ? blocks : [{ id: Date.now(), type: 'text', content: '' }]
        }));
      }
    } catch (error) {
      console.error('Document conversion error:', error);
      alert(`Failed to convert ${ext.toUpperCase()} file: ${error.message}`);
    } finally {
      setUploadingDoc(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    
    // Try to get HTML content first (preserves formatting from Word/websites)
    const htmlContent = clipboardData.getData('text/html');
    
    if (htmlContent) {
      const blocks = htmlToBlocks(htmlContent);
      setFormData(prev => ({ ...prev, content: blocks }));
      setShowPasteModal(false);
    } else {
      // Fallback to plain text and parse as markdown
      const textContent = clipboardData.getData('text/plain');
      if (textContent) {
        const blocks = parseMarkdownToBlocks(textContent);
        setFormData(prev => ({ ...prev, content: blocks }));
        setShowPasteModal(false);
      }
    }
  };

  const handleDelete = async () => {
    const itemType = contentType === 'script' ? 'script' : contentType === 'article' ? 'article' : contentType === 'tool' ? 'tool' : 'FAQ';
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      try {
        console.log(`Deleting ${itemType} with ID:`, id);
        
        if (contentType === 'script') {
          await deleteScript(id);
        } else if (contentType === 'article') {
          await deleteArticle(id);
        } else if (contentType === 'tool') {
          await deleteTool(id);
        } else {
          await deleteFaq(id);
        }
        
        console.log(`${itemType} deleted successfully`);
        navigate('/admin');
      } catch (error) {
        console.error('Failed to delete:', error);
        alert(`Failed to delete ${itemType}. Error: ${error.message}`);
      }
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1 className="page-title">
            {isEditing ? `Edit ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : contentType === 'tool' ? 'Tool' : 'FAQ'}` : `Add New ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : contentType === 'tool' ? 'Tool' : 'FAQ'}`}
          </h1>
        </div>
        
        {isEditing && (
          <button onClick={handleDelete} className="btn btn-danger">
            <Trash2 size={16} />
            Delete {contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : contentType === 'tool' ? 'Tool' : 'FAQ'}
          </button>
        )}
      </div>

      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
        <form onSubmit={handleSubmit}>
          {contentType === 'faq' ? (
            <div className="form-group">
              <label className="form-label">Question</label>
              <textarea
                name="question"
                className="form-textarea"
                value={formData.question}
                onChange={handleInputChange}
                required
                placeholder="Enter the FAQ question"
                rows="2"
              />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">{contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'Tool'} Title</label>
              <input
                type="text"
                name="title"
                className="form-input"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder={`Enter ${contentType} title`}
              />
            </div>
          )}

          {contentType !== 'faq' && (
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder={`Enter ${contentType} description`}
                rows="3"
              />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: contentType === 'faq' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {Object.keys(categories).map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Subcategory</label>
              <select
                name="subcategory"
                className="form-input"
                value={formData.subcategory}
                onChange={handleInputChange}
                required
              >
                <option value="">Select subcategory</option>
                {categories[formData.category]?.map(subcategory => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
              </select>
            </div>

            {contentType === 'faq' && (
              <div className="form-group">
                <label className="form-label">Difficulty</label>
                <select
                  name="difficulty"
                  className="form-input"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            )}
          </div>

          {contentType === 'article' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">Author</label>
                <input
                  type="text"
                  name="author"
                  className="form-input"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter author name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Read Time</label>
                <input
                  type="text"
                  name="readTime"
                  className="form-input"
                  value={formData.readTime}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 10 min read"
                />
              </div>
            </div>
          )}

          {contentType === 'tool' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Author</label>
                  <input
                    type="text"
                    name="author"
                    className="form-input"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Enter author name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Version</label>
                  <input
                    type="text"
                    name="version"
                    className="form-input"
                    value={formData.version}
                    onChange={handleInputChange}
                    placeholder="e.g., 1.0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Platform</label>
                  <select
                    name="platform"
                    className="form-input"
                    value={formData.platform}
                    onChange={handleInputChange}
                  >
                    <option value="Windows">Windows</option>
                    <option value="Linux">Linux</option>
                    <option value="macOS">macOS</option>
                    <option value="Cross-platform">Cross-platform</option>
                    <option value="Azure">Azure</option>
                    <option value="Docker">Docker</option>
                  </select>
                </div>
              </div>

              {/* Tool File Upload */}
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Package size={18} />
                  Downloadable Tool File (.exe, .zip, .msi, etc.)
                </label>
                <div style={{ 
                  border: '2px dashed #17a2b8', borderRadius: '10px', 
                  padding: '20px', textAlign: 'center', background: '#f8f9fa',
                  marginBottom: '10px'
                }}>
                  <input
                    type="file"
                    accept=".exe,.zip,.msi,.rar,.7z,.ps1,.bat,.cmd,.py,.jar,.tar,.gz,.tar.gz"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploadingToolFile(true);
                      try {
                        const result = await ApiService.uploadToolFile(file);
                        setFormData(prev => ({
                          ...prev,
                          fileName: result.fileName,
                          fileUrl: result.fileUrl,
                          fileSize: result.fileSize
                        }));
                        alert('File uploaded successfully!');
                      } catch (error) {
                        console.error('File upload failed:', error);
                        alert('File upload failed: ' + error.message);
                      } finally {
                        setUploadingToolFile(false);
                      }
                    }}
                    className="file-input"
                    id="tool-file-upload"
                    style={{ display: 'none' }}
                  />
                  {formData.fileUrl ? (
                    <div>
                      <div style={{ marginBottom: '10px', color: '#28a745', fontWeight: '600', fontSize: '1rem' }}>
                        ✅ File Attached
                      </div>
                      <div style={{ color: '#495057', marginBottom: '5px' }}>
                        <strong>File:</strong> {formData.fileName}
                      </div>
                      {formData.fileSize && (
                        <div style={{ color: '#6c757d', marginBottom: '10px', fontSize: '0.9rem' }}>
                          <strong>Size:</strong> {formData.fileSize}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <label htmlFor="tool-file-upload" className="btn btn-secondary" style={{ opacity: uploadingToolFile ? 0.6 : 1, cursor: uploadingToolFile ? 'wait' : 'pointer' }}>
                          <Upload size={16} />
                          {uploadingToolFile ? 'Uploading...' : 'Replace File'}
                        </label>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, fileName: '', fileUrl: '', fileSize: '' }))} className="btn" style={{ background: '#dc3545', color: 'white', border: 'none' }}>
                          <Trash2 size={16} />
                          Remove File
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Package size={36} color="#17a2b8" style={{ marginBottom: '10px' }} />
                      <div style={{ color: '#495057', marginBottom: '10px' }}>
                        Upload the tool/utility file that users will download
                      </div>
                      <label htmlFor="tool-file-upload" className="btn btn-primary" style={{ opacity: uploadingToolFile ? 0.6 : 1, cursor: uploadingToolFile ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Upload size={16} />
                        {uploadingToolFile ? 'Uploading...' : 'Choose File to Upload'}
                      </label>
                      <div style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '8px' }}>
                        Supported: .exe, .zip, .msi, .rar, .7z, .ps1, .bat, .py, .jar (Max 200MB)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">
              {contentType === 'script' ? 'SQL Script Content' : contentType === 'article' ? 'Article Content' : contentType === 'tool' ? 'Tool Information & Instructions' : 'Answer'}
            </label>
            
            <div className="upload-section" style={{ marginBottom: '15px' }}>
              <input
                type="file"
                accept={contentType === 'script' ? '.sql,.txt,.md' : '.md,.txt'}
                onChange={(e) => handleFileUpload(e.target.files[0], contentType)}
                className="file-input"
                id={`${contentType}-upload`}
                style={{ display: 'none' }}
              />
              <label htmlFor={`${contentType}-upload`} className="btn btn-secondary">
                <Upload size={16} />
                Upload {contentType === 'script' ? 'SQL' : (contentType === 'article' || contentType === 'tool') ? 'Markdown (.md)' : 'Text'} File
              </label>
              {(contentType === 'article' || contentType === 'tool') && (
                <>
                  <input
                    type="file"
                    accept=".docx,.pdf"
                    onChange={(e) => handleDocUpload(e.target.files[0])}
                    className="file-input"
                    id="doc-upload"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="doc-upload" className="btn btn-primary" style={{ opacity: uploadingDoc ? 0.6 : 1, pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                    <FileText size={16} />
                    {uploadingDoc ? 'Converting...' : 'Upload Word / PDF'}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPasteModal(true)}
                    className="btn btn-success"
                  >
                    <Clipboard size={16} />
                    Paste Content
                  </button>
                </>
              )}
              <button 
                type="button" 
                onClick={() => setShowGuidelines(!showGuidelines)} 
                className="btn btn-info"
              >
                📋 Format Guidelines
              </button>
            </div>

            {showPasteModal && (contentType === 'article' || contentType === 'tool') && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', zIndex: 1000
              }}>
                <div style={{
                  background: 'white', borderRadius: '12px', padding: '30px',
                  width: '700px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column'
                }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>Paste {contentType === 'tool' ? 'Tool' : 'Article'} Content</h3>
                  <p style={{ color: '#666', margin: '0 0 15px 0', fontSize: '0.9rem' }}>
                    Copy content from Word, a website, or any source and paste it below (Ctrl+V). 
                    Headings, bold, italic, bullets, tables, and images will be automatically detected.
                  </p>
                  <div
                    contentEditable
                    onPaste={handlePaste}
                    style={{
                      border: '2px dashed #28a745', borderRadius: '8px', padding: '20px',
                      minHeight: '200px', maxHeight: '50vh', overflowY: 'auto',
                      fontSize: '1rem', lineHeight: '1.6', outline: 'none',
                      color: '#999', background: '#f9f9f9'
                    }}
                    suppressContentEditableWarning
                    data-placeholder="Click here and press Ctrl+V to paste your content..."
                    onFocus={(e) => {
                      e.target.style.borderColor = '#28a745';
                      e.target.style.background = '#fff';
                      if (!e.target.textContent.trim()) {
                        e.target.innerHTML = '';
                      }
                    }}
                  >
                    Click here and press Ctrl+V to paste your content...
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
                    <button type="button" onClick={() => setShowPasteModal(false)} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {showGuidelines && (
              <div className="guidelines-panel" style={{ marginBottom: '15px' }}>
                <h4>{contentType === 'script' ? 'SQL Script' : contentType === 'article' ? 'Article' : 'FAQ'} Format Guidelines</h4>
                
                {contentType === 'article' ? (
                  <div className="format-section">
                    <h5>📄 Markdown (.md) File Format</h5>
                    <div className="format-rules">
                      <strong>Headings:</strong><br/>
                      <code># Heading 1 (Main Title)</code><br/>
                      <code>## Heading 2 (Section)</code><br/>
                      <code>### Heading 3 (Sub-section)</code><br/><br/>
                      
                      <strong>Text Formatting:</strong><br/>
                      <code>**bold text**</code><br/>
                      <code>*italic text*</code><br/>
                      <code>- Bullet points</code><br/>
                      <code>1. Numbered lists</code><br/><br/>
                      
                      <strong>SQL Code Blocks:</strong><br/>
                      <code>```sql</code><br/>
                      <code>SELECT * FROM sys.databases;</code><br/>
                      <code>```</code><br/><br/>
                      
                      <strong>Tables:</strong><br/>
                      <code>| Header 1 | Header 2 |</code><br/>
                      <code>|----------|----------|</code><br/>
                      <code>| Data 1   | Data 2   |</code>
                    </div>
                  </div>
                ) : contentType === 'script' ? (
                  <div className="format-section">
                    <h5>📄 SQL Script Files (.sql, .txt)</h5>
                    <div className="format-rules">
                      <strong>File Structure:</strong>
                      <code>-- Script Title and Description</code><br/>
                      <code>-- Author: Your Name</code><br/>
                      <code>-- Purpose: What this script does</code><br/><br/>
                      
                      <strong>SQL Best Practices:</strong>
                      • Use clear comments with --<br/>
                      • Include error handling where needed<br/>
                      • Use proper indentation<br/>
                      • Test scripts before uploading<br/>
                      • Include sample output if helpful<br/><br/>
                      
                      <strong>Example Format:</strong>
                      <code>-- CPU Usage Monitoring Script</code><br/>
                      <code>-- Identifies high CPU consuming queries</code><br/>
                      <code></code><br/>
                      <code>SELECT TOP 10</code><br/>
                      <code>    qs.sql_handle,</code><br/>
                      <code>    qs.total_worker_time</code><br/>
                      <code>FROM sys.dm_exec_query_stats qs;</code>
                    </div>
                  </div>
                ) : (
                  <div className="format-section">
                    <h5>❓ FAQ Content Files (.txt, .md)</h5>
                    <div className="format-rules">
                      <strong>Text Format:</strong>
                      • Use clear, concise language<br/>
                      • Include step-by-step instructions<br/>
                      • Add code examples where relevant<br/>
                      • Use bullet points for lists<br/><br/>
                      
                      <strong>Markdown Support:</strong>
                      <code>**Bold text**</code><br/>
                      <code>*Italic text*</code><br/>
                      <code>`code snippets`</code><br/>
                      <code>- Bullet points</code><br/>
                      <code>1. Numbered lists</code><br/><br/>
                      
                      <strong>SQL in FAQ:</strong>
                      <code>```sql</code><br/>
                      <code>SELECT * FROM table;</code><br/>
                      <code>```</code>
                    </div>
                  </div>
                )}
                
                <div className="format-section">
                  <h5>✅ Best Practices</h5>
                  <div className="format-rules">
                    • Keep content focused and relevant<br/>
                    • Use proper grammar and spelling<br/>
                    • Test all code before submitting<br/>
                    • Include context and explanations<br/>
                    • Follow naming conventions<br/>
                    • Add appropriate difficulty level
                  </div>
                </div>
              </div>
            )}
            
            {(contentType === 'article' || contentType === 'tool') ? (
              <RichEditor
                content={formData.content}
                onChange={(blocks) => setFormData(prev => ({ ...prev, content: blocks }))}
              />
            ) : (
              <textarea
                name={contentType === 'faq' ? 'answer' : 'content'}
                className="form-textarea"
                value={contentType === 'faq' ? formData.answer : formData.content}
                onChange={handleInputChange}
                required
                placeholder={contentType === 'script' ? 'Enter your SQL script here...' : contentType === 'faq' ? 'Enter the detailed answer...' : 'Enter your article content here...'}
                rows={contentType === 'faq' ? '10' : '20'}
                style={contentType === 'script' ? { fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: '0.9rem' } : {}}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => navigate('/admin')} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <Save size={16} />
              {isEditing ? `Update ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : contentType === 'tool' ? 'Tool' : 'FAQ'}` : `Save ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : contentType === 'tool' ? 'Tool' : 'FAQ'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEditor;